import { useState, useCallback } from 'react';
import { AssessmentData, SMSAutoResponse } from 'domains/content/types';
import { SmsSaveData, SmsSaveMode } from './useSmsSubmission';
import { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText';

type UseAssessmentHandlingProps = {
    initialAssessmentData: AssessmentData;
    isNameTooShort: boolean;
    isUpdate: boolean;
    handleSave: (saveData: SmsSaveData) => void;
};

const useAssessmentHandling = ({
    initialAssessmentData,
    isNameTooShort,
    isUpdate,
    handleSave,
}: UseAssessmentHandlingProps) => {
    const [assessmentData, setAssessmentData] = useState<AssessmentData>({
        assessmentId: initialAssessmentData?.assessmentId || '',
        assessmentName: initialAssessmentData?.assessmentName || '',
        assessmentConsentRequired: initialAssessmentData?.assessmentConsentRequired ?? true,
    });

    const handleSaveWithAssessment = useCallback(
        ({
            name,
            content,
            saveMode,
            overrideAssessmentData,
            autoResponses,
            status,
        }: {
            name: string;
            content: string;
            saveMode: SmsSaveMode;
            overrideAssessmentData?: AssessmentData;
            autoResponses?: SMSAutoResponse[];
            status?: ContentStatus;
        }) => {
            const dataToSave = overrideAssessmentData || assessmentData;

            handleSave({
                name,
                content,
                isNameTooShort,
                isUpdate,
                saveMode,
                assessmentData: dataToSave,
                autoResponses,
                status,
            });
        },
        [assessmentData, handleSave, isNameTooShort, isUpdate]
    );

    const handleSaveAndExitWithAssessment = useCallback(
        ({
            name,
            content,
            overrideAssessmentData,
            autoResponses,
            status,
        }: {
            name: string;
            content: string;
            overrideAssessmentData?: AssessmentData;
            autoResponses?: SMSAutoResponse[];
            status?: ContentStatus;
        }) =>
            handleSaveWithAssessment({
                name,
                content,
                saveMode: SmsSaveMode.EXIT,
                overrideAssessmentData,
                autoResponses,
                status,
            }),
        [handleSaveWithAssessment]
    );

    const handleSaveNameWithAssessment = useCallback(
        ({
            name,
            content,
            overrideAssessmentData,
            autoResponses,
        }: {
            name: string;
            content: string;
            overrideAssessmentData?: AssessmentData;
            autoResponses?: SMSAutoResponse[];
        }) =>
            handleSaveWithAssessment({
                name,
                content,
                saveMode: SmsSaveMode.SAVE,
                overrideAssessmentData,
                autoResponses,
            }),
        [handleSaveWithAssessment]
    );

    return {
        assessmentData,
        setAssessmentData,
        handleSaveWithAssessment,
        handleSaveAndExitWithAssessment,
        handleSaveNameWithAssessment,
    };
};

export default useAssessmentHandling;
